import { financialEvents } from '@api/api/financial-events/financial-events';
import {
  KeyDevsObject,
  KeyDevsResponse,
} from '@api/api/financial-events/key-developments-events';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { useQuery } from '@tanstack/react-query';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';
import { formatInTimeZone } from 'date-fns-tz';
import { useMemo } from 'react';

import type { GuidanceDirection } from '../earnings-reporting-summary/types';
import type { FilterableEarningsRow } from './filterReportedCompanies';

const QUERY_KEY_REPORTED_COMPANIES_GUIDANCE =
  'QUERY_KEY_REPORTED_COMPANIES_GUIDANCE';

const GUIDANCE_KEY_DEV_TYPES = [
  'corporate_guidance_raised',
  'corporate_guidance_lowered',
];

const PAGE_SIZE = 500;
const MAX_DAYS_FROM_REPORTING_DATE = 1;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_MS_FROM_REPORTING_DATE = MAX_DAYS_FROM_REPORTING_DATE * MS_PER_DAY;

interface FetchGuidanceKeyDevsProps {
  entityTags: string[];
  startDate: string;
  endDate: string;
  signal?: AbortSignal;
}

const fetchGuidanceKeyDevs = async ({
  entityTags,
  startDate,
  endDate,
  signal,
}: FetchGuidanceKeyDevsProps) => {
  const { path, schema } = financialEvents.postKeyDevs;
  const keyDevs: KeyDevsObject[] = [];

  for (let page = 1; ; ) {
    const response = await wretchRequest<KeyDevsResponse>(path, {
      method: 'post',
      schema,
      signal,
      body: {
        entities: entityTags,
        filters: [
          {
            types: GUIDANCE_KEY_DEV_TYPES,
            start_date: startDate,
            end_date: endDate,
          },
        ],
        page: { number: page, size: PAGE_SIZE },
      },
    });

    keyDevs.push(...response.key_devs);

    if (!response.page.next) {
      break;
    }

    page = response.page.next;
  }

  return keyDevs;
};

const getGuidanceDirection = (
  eventType: string
): GuidanceDirection | undefined => {
  const normalisedEventType = eventType.toLowerCase();

  if (normalisedEventType.includes('raised')) {
    return 'raised';
  }

  if (normalisedEventType.includes('lowered')) {
    return 'lowered';
  }

  return undefined;
};

// reporting_date and effective_date are UTC instants, and the key devs endpoint
// filters on UTC days. Local calendar math shifts both the requested window and
// the row matching by a day for non-UTC users, dropping boundary events.
const startOfDayUtcMs = (date: string) => {
  const utcDate = new Date(date);
  utcDate.setUTCHours(0, 0, 0, 0);

  return utcDate.getTime();
};

const formatUtcDay = (timestamp: number) =>
  formatInTimeZone(timestamp, 'UTC', 'yyyy-MM-dd');

const getReportingWindow = (rows: FilterableEarningsRow[]) => {
  const timestamps = rows.map(row => startOfDayUtcMs(row.reporting_date));

  if (!timestamps.length) {
    return undefined;
  }

  return {
    startDate: formatUtcDay(
      Math.min(...timestamps) - MAX_MS_FROM_REPORTING_DATE
    ),
    endDate: formatUtcDay(Math.max(...timestamps) + MAX_MS_FROM_REPORTING_DATE),
  };
};

export const useReportedCompaniesGuidance = (rows: FilterableEarningsRow[]) => {
  const entityTags = [...new Set(rows.map(row => row.entity_tag))].sort();
  const reportingWindow = getReportingWindow(rows);

  const { data: keyDevs } = useQuery({
    queryKey: [
      QUERY_KEY_REPORTED_COMPANIES_GUIDANCE,
      entityTags,
      reportingWindow?.startDate,
      reportingWindow?.endDate,
    ],
    queryFn: ({ signal }) =>
      fetchGuidanceKeyDevs({
        entityTags,
        startDate: reportingWindow?.startDate ?? '',
        endDate: reportingWindow?.endDate ?? '',
        signal,
      }),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: !!entityTags.length && !!reportingWindow,
  });

  return useMemo(() => {
    const guidanceByEarningsId = new Map<string, GuidanceDirection>();

    for (const row of rows) {
      const keyDev = keyDevs?.find(
        event =>
          event.entity_tag === row.entity_tag &&
          Math.abs(
            startOfDayUtcMs(event.effective_date) -
              startOfDayUtcMs(row.reporting_date)
          ) <= MAX_MS_FROM_REPORTING_DATE
      );
      const guidance = keyDev && getGuidanceDirection(keyDev.event_type);

      if (guidance) {
        guidanceByEarningsId.set(row.id, guidance);
      }
    }

    return guidanceByEarningsId;
  }, [rows, keyDevs]);
};
